import type { GridGeometry } from './types';

export interface ProjectionConfig {
  scale: number;
  stepXFactor: number;
  stepYFactor: number;
  renderWidthFactor: number;
  renderHeightFactor: number;
}

interface ScaleConfig {
  scale: number;
}

interface GridFactoryBase {
  directions: number;
  facingAngles: number[];
  rotationStep: number;
  movementAngleTolerance: number;
  keyDirections: Record<string, { dx: number; dy: number; facing: number }>;
  neighbors(q: number, r: number): Array<{ x: number; y: number }>;
  distance(x1: number, y1: number, x2: number, y2: number): number;
  isAdjacent(x1: number, y1: number, x2: number, y2: number): boolean;
  drawTilePath(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    q?: number,
    r?: number
  ): void;
}

export interface GridFactoryOptions extends GridFactoryBase {
  projection: ProjectionConfig;
  tileToPixel?: (q: number, r: number, size: number) => { x: number; y: number };
  pixelToTile?: (px: number, py: number, size: number) => { q: number; r: number };
  mapPixelDimensions?: (width: number, height: number, size: number) => { w: number; h: number };
  tileCentroid?: (size: number, q?: number, r?: number) => { x: number; y: number };
  fillTile?: (ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, q?: number, r?: number) => void;
  snapFacing?: (angle: number) => number;
  getNextEdgeFacing?: (currentFacing: number, direction: 'left' | 'right') => number;
  resolveTurnFacingForKey?: (key: string, x: number, y: number, currentFacing: number) => number | null;
}

export interface ResolvedProjection {
  stepX: number;
  stepY: number;
  renderWidth: number;
  renderHeight: number;
  offsetX: number;
  offsetY: number;
}

export function resolveProjection(size: number, config: ProjectionConfig): ResolvedProjection {
  const scaled = size * config.scale;
  const renderWidth = scaled * config.renderWidthFactor;
  const renderHeight = scaled * config.renderHeightFactor;
  return {
    stepX: scaled * config.stepXFactor,
    stepY: scaled * config.stepYFactor,
    renderWidth,
    renderHeight,
    // Keep the shape centered relative to the renderer's base tile box.
    offsetX: (renderWidth - size) * 0.5,
    offsetY: (renderHeight - size) * 0.5,
  };
}

export function projectTileToPixel(q: number, r: number, projection: ResolvedProjection): { x: number; y: number } {
  return {
    x: q * projection.stepX + projection.offsetX,
    y: r * projection.stepY + projection.offsetY,
  };
}

export function projectPixelToTile(px: number, py: number, projection: ResolvedProjection): { q: number; r: number } {
  return {
    q: Math.floor((px - projection.offsetX) / projection.stepX),
    r: Math.floor((py - projection.offsetY) / projection.stepY),
  };
}

export function projectMapDimensions(width: number, height: number, projection: ResolvedProjection): { w: number; h: number } {
  return {
    w: (width - 1) * projection.stepX + projection.renderWidth,
    h: (height - 1) * projection.stepY + projection.renderHeight,
  };
}

export function createGridGeometry(options: GridFactoryOptions): GridGeometry {
  const defaultTileToPixel = (q: number, r: number, size: number) =>
    projectTileToPixel(q, r, resolveProjection(size, options.projection));
  const defaultPixelToTile = (px: number, py: number, size: number) =>
    projectPixelToTile(px, py, resolveProjection(size, options.projection));
  const defaultMapPixelDimensions = (width: number, height: number, size: number) =>
    projectMapDimensions(width, height, resolveProjection(size, options.projection));
  const defaultTileCentroid = (size: number) => ({ x: size / 2, y: size / 2 });
  const defaultFillTile = (ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, q?: number, r?: number) => {
    options.drawTilePath(ctx, cx, cy, size, q, r);
    ctx.fill();
  };
  const defaultSnapFacing = (angle: number) => {
    const normalized = ((angle % 360) + 360) % 360;
    const index = Math.round(normalized / options.rotationStep) % options.facingAngles.length;
    return index * options.rotationStep;
  };
  const defaultGetNextEdgeFacing = (currentFacing: number, direction: 'left' | 'right') => {
    const snapped = defaultSnapFacing(currentFacing);
    const step = direction === 'left' ? -options.rotationStep : options.rotationStep;
    return defaultSnapFacing(snapped + step);
  };
  const defaultResolveTurnFacingForKey = (_key: string, _x: number, _y: number, _currentFacing: number) => null;

  return {
    directions: options.directions,
    facingAngles: options.facingAngles,
    rotationStep: options.rotationStep,
    movementAngleTolerance: options.movementAngleTolerance,
    keyDirections: options.keyDirections,
    tileToPixel: options.tileToPixel ?? defaultTileToPixel,
    pixelToTile: options.pixelToTile ?? defaultPixelToTile,
    neighbors: options.neighbors,
    distance: options.distance,
    isAdjacent: options.isAdjacent,
    drawTilePath: options.drawTilePath,
    fillTile: options.fillTile ?? defaultFillTile,
    tileCentroid: options.tileCentroid ?? defaultTileCentroid,
    mapPixelDimensions: options.mapPixelDimensions ?? defaultMapPixelDimensions,
    snapFacing: options.snapFacing ?? defaultSnapFacing,
    getNextEdgeFacing: options.getNextEdgeFacing ?? defaultGetNextEdgeFacing,
    resolveTurnFacingForKey: options.resolveTurnFacingForKey ?? defaultResolveTurnFacingForKey,
  };
}

/** Scale the renderer's half-tile size parameter to actual shape extent. */
export function scaleTileExtent(size: number, config: ScaleConfig): number {
  return size * config.scale;
}

/** Scale full tile size to shape radius for pixel-to-tile math. */
export function scaleTileRadius(size: number, config: ScaleConfig): number {
  return size * 0.5 * config.scale;
}
